import { useState, useEffect, useRef } from "react";
import api from "../../api/client";
import { useConfirm } from "../../context/ConfirmContext";

/**
 * NapQrModal handles:
 * - Preview mode (after clicking Generate QR): shows preview + Used Port/Vacant/ONU inputs + Save/Cancel
 * - View mode (clicking existing QR thumbnail): shows enlarged QR + Used Port/Vacant/ONU + Update/Delete/No Record
 */
export default function NapQrModal({ record, mode, previewData, onClose, onSaved, canDeleteQr, canEdit }) {
  const { confirm } = useConfirm();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showNoStencilConfirm, setShowNoStencilConfirm] = useState(false);
  const [error, setError] = useState("");
  const [usedPort, setUsedPort] = useState(record?.used_port ?? "");
  const [vacant, setVacant] = useState(record?.vacant ?? "");
  const [onuConnect, setOnuConnect] = useState(record?.onu_connect ?? "");
  const onuManuallyEdited = useRef(false);

  const qrData = mode === "preview" ? previewData?.qr_code_data : record?.qr_code_data;

  // Auto-compute ONU Connect when Used Port or Vacant changes (unless user manually edited ONU)
  useEffect(() => {
    if (onuManuallyEdited.current) return;
    const ports = record?.no_of_ports;
    const u = usedPort === "" ? null : parseInt(usedPort, 10);
    const v = vacant === "" ? null : parseInt(vacant, 10);
    if (ports != null && u != null && v != null) {
      const computed = Math.max(0, ports - u - v);
      setOnuConnect(String(computed));
    }
  }, [usedPort, vacant, record?.no_of_ports]);

  const handleSave = async () => {
    if (usedPort === "" || usedPort === null) {
      setError("Used Port is required.");
      return;
    }
    if (vacant === "" || vacant === null) {
      setError("Unused/Vacant Port is required.");
      return;
    }
    if (onuConnect === "" || onuConnect === null) {
      setError("ONU Connect is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const body = {
        used_port: parseInt(usedPort, 10),
        vacant: parseInt(vacant, 10),
        onu_connect: parseInt(onuConnect, 10),
      };
      await api.post(`/nap-locator/${record.id}/qr/save`, body);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (usedPort === "" || usedPort === null) {
      setError("Used Port is required.");
      return;
    }
    if (vacant === "" || vacant === null) {
      setError("Unused/Vacant Port is required.");
      return;
    }
    if (onuConnect === "" || onuConnect === null) {
      setError("ONU Connect is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const body = {
        used_port: parseInt(usedPort, 10),
        vacant: parseInt(vacant, 10),
        onu_connect: parseInt(onuConnect, 10),
      };
      await api.post(`/nap-locator/${record.id}/qr/save`, body);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: "Delete QR Code",
      message: `Delete QR Code for "${record.nap_code}"? The record will return to 'Generate QR' state.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    setDeleting(true);
    setError("");
    try {
      await api.delete(`/nap-locator/${record.id}/qr`);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete QR Code.");
    } finally {
      setDeleting(false);
    }
  };

  const handleNoStencilConfirm = async () => {
    setSaving(true);
    setError("");
    try {
      await api.post(`/nap-locator/${record.id}/no-stencil`);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to mark No Stencil.");
      setShowNoStencilConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleNumericInput = (setter) => (e) => {
    const val = e.target.value;
    if (val === "" || (/^\d{1,2}$/.test(val) && parseInt(val, 10) >= 0)) {
      setter(val);
    }
  };

  // No Stencil confirmation popup
  if (showNoStencilConfirm) {
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-xs mx-4 p-6">
          <h2 className="text-lg font-semibold mb-4">No Record</h2>
          <p className="text-sm text-gray-600 mb-6">
            Confirm that this NAP code has no record?
          </p>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowNoStencilConfirm(false)}
              className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
            >
              No
            </button>
            <button
              onClick={handleNoStencilConfirm}
              disabled={saving}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Yes"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-lg font-semibold">
            {mode === "preview" ? "QR Code Preview" : "QR Code"}
          </h2>
          <span className="text-sm text-gray-600 font-medium">{record?.nap_code}</span>
        </div>

        {/* QR Image */}
        {qrData && (
          <div className="flex justify-center mb-4">
            <img
              src={`data:image/png;base64,${qrData}`}
              alt="QR Code"
              className="w-64 h-64 border rounded"
            />
          </div>
        )}

        {/* Used Port, Vacant & ONU Connect fields */}
        {mode === "preview" || editing ? (
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Used Port <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={usedPort}
                onChange={handleNumericInput(setUsedPort)}
                placeholder="0-99"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Unused/Vacant <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={vacant}
                onChange={handleNumericInput(setVacant)}
                placeholder="0-99"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">ONU Connect <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={onuConnect}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "" || (/^\d{1,2}$/.test(val) && parseInt(val, 10) >= 0)) {
                    onuManuallyEdited.current = true;
                    setOnuConnect(val);
                  }
                }}
                placeholder="0-99"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-gray-50 rounded p-2">
              <span className="text-xs text-gray-500">Used Port</span>
              <p className="text-sm font-medium">{record?.used_port ?? "—"}</p>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <span className="text-xs text-gray-500">Unused/Vacant</span>
              <p className="text-sm font-medium">{record?.vacant ?? "—"}</p>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <span className="text-xs text-gray-500">ONU Connect</span>
              <p className="text-sm font-medium">{record?.onu_connect ?? "—"}</p>
            </div>
          </div>
        )}

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

        {/* Actions */}
        <div className="flex justify-end gap-2">
          {mode === "preview" ? (
            <>
              <button
                onClick={() => setShowNoStencilConfirm(true)}
                className="px-4 py-2 text-sm bg-orange-500 text-white rounded hover:bg-orange-600"
              >
                No Record
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save QR Code"}
              </button>
            </>
          ) : editing ? (
            <>
              <button
                onClick={() => { setEditing(false); setUsedPort(record?.used_port ?? ""); setVacant(record?.vacant ?? ""); setOnuConnect(record?.onu_connect ?? ""); onuManuallyEdited.current = false; }}
                className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={saving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          ) : (
            <>
              {canDeleteQr && (
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 text-sm bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                >
                  {deleting ? "Deleting…" : "Delete QR Code"}
                </button>
              )}
              {canEdit && (
                <button
                  onClick={() => setEditing(true)}
                  className="px-4 py-2 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600"
                >
                  Update
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
